import { serverNow } from "../../../../../../../../shared/frontend/time/serverClock";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Space, StatusText, Typography } from "../../../../../../../../shared/frontend/ui";
import type { ApiClient } from "../../../../app/api/client";
import { spacing } from "../../../../app/design-system";
import { formatDateOnly } from "../../../../app/units/format";
import type { UnitsSettings } from "../../../../app/units/units";
import { globalKey } from "../../../../app/query/queryKeys";
import { SummaryListContent } from "../../../../components/SummaryListContent";
import { MemberCareCaseCreateDrawer } from "../../../../components/persons/MemberCareCaseCreateDrawer";
import { DashboardListDate } from "../../../dashboard/DashboardListDate";
import { useMemberCareAccess, useMemberCareSummary } from "./personOverviewCareData";
import { ISO_DATE_LENGTH } from "../../../../app/utils/isoDate";

type PipelineOption = { id: string; name: string; isActive?: boolean; kind?: number };
type AccountOption = { id: string; displayName: string; isActive?: boolean };

const LIST_CONTENT_STYLE: React.CSSProperties = { flex: 1, minWidth: 0, display: "block", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" };

type Props = { personId: string; api: ApiClient; text: (key: string) => string; units: UnitsSettings };

/**
 * Block „Betreuung“: offene Betreuungsvorgänge (je Episode eine Zeile) mit Wiedervorlage, Phase und
 * Zuständigem; darunter die Zahl der abgeschlossenen. Ohne offenen Vorgang der Leerzustand.
 */
export function PersonMemberCareBody({ personId, api, text, units }: Props) {
  const nav = useNavigate();
  const { canRead } = useMemberCareAccess();
  const summaryQuery = useMemberCareSummary(api, personId, canRead);
  const summary = summaryQuery.data;
  const [now] = React.useState(() => serverNow());
  return (
    <>
      <SummaryListContent
        items={summary?.open ?? []}
        emptyText={text("No open member care cases")}
        getItemKey={(careCase) => careCase.id}
        renderItem={(careCase) => {
          const details = [careCase.phaseName, careCase.assignedAccountName].filter(Boolean).join(" · ");
          const due = careCase.nextFollowUpAt != null && new Date(careCase.nextFollowUpAt).getTime() <= now;
          return (
            <>
              <DashboardListDate value={careCase.nextFollowUpAt ?? careCase.createdAt} units={units} />
              <span style={LIST_CONTENT_STYLE}>
                <Typography.Link strong onClick={() => nav(`/member-care/${encodeURIComponent(careCase.id)}`)}>{careCase.pipelineName ?? "—"}</Typography.Link>
                {details ? <Typography.Text type="secondary"> · {details}</Typography.Text> : null}
                {due ? <> · <StatusText color="warning">{text("Follow-up")}</StatusText></> : null}
              </span>
            </>
          );
        }}
      />
      {summary && summary.closedCount > 0 ? (
        <Typography.Text type="secondary" style={{ display: "block", marginTop: spacing.sm }}>
          {text("Closed member care cases: {0}").replace("{0}", String(summary.closedCount))}
          {summary.lastClosed?.closedAt
            ? ` — ${text("last {0} on {1}").replace("{0}", summary.lastClosed.pipelineName ?? "").replace("{1}", formatDateOnly(summary.lastClosed.closedAt.slice(0, ISO_DATE_LENGTH), units))}`
            : ""}
        </Typography.Text>
      ) : null}
    </>
  );
}

/** Kopfaktionen des Blocks „Betreuung“: Kontakt erfassen (bei genau einem Vorgang) und Vorgang anlegen. */
export function PersonMemberCareActions({ personId, personName, api, text }: Omit<Props, "units"> & { personName: string }) {
  const nav = useNavigate();
  const [createOpen, setCreateOpen] = React.useState(false);
  const { canRead, canWrite } = useMemberCareAccess();
  const summaryQuery = useMemberCareSummary(api, personId, canRead);
  const lookupsQuery = useQuery({
    queryKey: globalKey("leads", "lookups", "member-care"),
    enabled: canWrite && createOpen,
    queryFn: () => api.get<{ pipelines?: PipelineOption[]; accounts?: AccountOption[] }>("/api/v1/leads/lookups?kind=1")
  });
  if (!canWrite) return null;
  const open = summaryQuery.data?.open ?? [];
  const openCase = (id: string) => nav(`/member-care/${encodeURIComponent(id)}`);
  return (
    <>
      <Space size={spacing.xs} wrap>
        {open.length === 1 ? <Button size="small" onClick={() => openCase(open[0]!.id)}>{text("Log contact")}</Button> : null}
        <Button size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>{text("New member care case")}</Button>
      </Space>
      <MemberCareCaseCreateDrawer
        open={createOpen}
        contentLoading={lookupsQuery.isLoading}
        personId={personId}
        personName={personName}
        pipelines={lookupsQuery.data?.pipelines ?? []}
        accounts={(lookupsQuery.data?.accounts ?? []).filter((a) => a.isActive !== false).map((a) => ({ id: a.id, name: a.displayName }))}
        onCancel={() => setCreateOpen(false)}
        onCreated={(leadId) => {
          setCreateOpen(false);
          void summaryQuery.refetch();
          openCase(leadId);
        }}
      />
    </>
  );
}
